import {
  BrowserDocxTemplatePreview,
  type PreviewVariableConfig,
} from "@/components/features/matter/file-repo/GenerateDocument/BrowserDocxTemplatePreview";
import { getFirmFileDisplayName } from "@/lib/utils/firmFile";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { useMemo } from "react";
import {
  SignatureRequestDocumentPreview,
  type SignatureRequestPreviewDocument,
} from "./SignatureRequestDocumentPreview";

type InteractiveSignatureRequestDocxPreviewProps = {
  buildInputs: () => Record<string, string>;
  canEditDocumentFiles: boolean;
  getVariableConfig: (variable: string) => PreviewVariableConfig;
  matterId: string;
  onVariableSelect: (variable: string, value: string) => void;
  selectedDocument: SignatureRequestPreviewDocument | null;
};

const canMapSignatureRequestVariable = (variable: string) =>
  !variable.startsWith("Signer") && !variable.startsWith("UserSigner");

const getSignatureRequestVariableHighlight = (variable: string) =>
  canMapSignatureRequestVariable(variable) ? null : "#fef08a";

export const InteractiveSignatureRequestDocxPreview = ({
  buildInputs,
  canEditDocumentFiles,
  getVariableConfig,
  matterId,
  onVariableSelect,
  selectedDocument,
}: InteractiveSignatureRequestDocxPreviewProps) => {
  const previewFile = useMemo(() => {
    if (!selectedDocument) return null;

    const { document } = selectedDocument;
    const fileName =
      selectedDocument.type === "firm-file"
        ? getFirmFileDisplayName(selectedDocument.document)
        : document.name;
    const normalizedFileName =
      selectedDocument.type === "matter-template" &&
      !getFileNameParts(fileName).fileExtension
        ? document.presignedUrlKey
        : fileName;

    if (
      !normalizedFileName ||
      getFileNameParts(normalizedFileName).fileExtension !== "docx" ||
      !document.presignedUrlKey
    ) {
      return null;
    }

    return {
      fileType: selectedDocument.type,
      templateFile: {
        fileName: normalizedFileName,
        presignedURLKey: document.presignedUrlKey,
      },
      templateId: `${selectedDocument.type}:${document.id}`,
    };
  }, [selectedDocument]);

  if (!previewFile) {
    return (
      <SignatureRequestDocumentPreview
        matterId={matterId}
        selectedDocument={selectedDocument}
        canEditDocumentFiles={canEditDocumentFiles}
        emptyTitle="No document selected."
        emptyDescription="Go back to the previous step to choose a document."
      />
    );
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <BrowserDocxTemplatePreview
        open
        flushLayout
        canSelectVariable={canMapSignatureRequestVariable}
        fileType={previewFile.fileType}
        templateId={previewFile.templateId}
        templateFile={previewFile.templateFile}
        buildInputs={buildInputs}
        getVariableConfig={getVariableConfig}
        getVariableHighlight={getSignatureRequestVariableHighlight}
        onVariableSelect={onVariableSelect}
      />
    </div>
  );
};
